import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowUpRight,
  Mail,
  CheckCircle,
  Loader2,
  Shield,
  Clock,
  MessageSquare } from
'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (err) {
      setError('Qualcosa è andato storto. Per favore riprova.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const trustElements = [
  {
    icon: <Clock className="w-5 h-5" />,
    title: 'Risposta in 24-48h',
    description: 'Rispondiamo a tutte le richieste entro 48 ore.'
  },
  {
    icon: <Shield className="w-5 h-5" />,
    title: 'Confidenziale',
    description: 'Le tue informazioni sono al sicuro e mai condivise.'
  },
  {
    icon: <MessageSquare className="w-5 h-5" />,
    title: 'Senza Impegno',
    description: 'Consulenza iniziale gratuita per discutere le tue esigenze.'
  }];


  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      {/* Hero Section */}
      <section className="pt-32 pb-12 relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#2DD4BF]/5 rounded-full blur-[100px]" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center max-w-2xl mx-auto">

            <span className="text-[#2DD4BF] text-sm font-semibold tracking-wider uppercase mb-4 block">
              Contattaci
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A1D24] leading-tight tracking-tight mb-6">
              Iniziamo una Conversazione
            </h1>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              Pronto a scalare il tuo business digitale? Compila il modulo qui sotto e ti risponderemo entro 48 ore.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Form */}
            <motion.div {...fadeUp}>
              <div className="bg-white border border-[#E5E7EB] p-8 md:p-12 rounded-lg shadow-lg">
                {isSubmitted ?
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12">

                    <div className="w-16 h-16 rounded-full bg-[#2DD4BF]/10 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-8 h-8 text-[#2DD4BF]" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#1A1D24] mb-4">
                      Messaggio Ricevuto!
                    </h3>
                    <p className="text-[#6B7280] mb-8">
                      Grazie per averci contattato. Esamineremo la tua richiesta e ti risponderemo entro 48 ore.
                    </p>
                    <button
                    onClick={() => setIsSubmitted(false)}
                    data-testid="contact-send-another"
                    className="text-[#2DD4BF] font-medium hover:underline">

                      Invia un altro messaggio
                    </button>
                  </motion.div> :

                <form onSubmit={handleSubmit} data-testid="contact-form">
                    <h2 className="text-2xl font-bold text-[#1A1D24] mb-2">
                      Richiedi una Consulenza
                    </h2>
                    <p className="text-[#6B7280] mb-8">
                      Raccontaci del tuo business e dei tuoi obiettivi di crescita.
                    </p>

                    <div className="space-y-6">
                      {/* Name Field */}
                      <div>
                        <label htmlFor="name" className="block text-[#1A1D24] text-sm font-medium mb-2">
                          Nome *
                        </label>
                        <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        data-testid="contact-input-name"
                        placeholder="Il tuo nome"
                        className="w-full bg-[#F9FAFB] border border-[#E5E7EB] text-[#1A1D24] placeholder-[#9CA3AF] rounded-lg h-12 px-4 focus:border-[#2DD4BF] focus:ring-1 focus:ring-[#2DD4BF] outline-none transition-colors" />

                      </div>

                      {/* Email Field */}
                      <div>
                        <label htmlFor="email" className="block text-[#1A1D24] text-sm font-medium mb-2">
                          Email *
                        </label>
                        <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="contact-input-email"
                        placeholder="la-tua@email.com"
                        className="w-full bg-[#F9FAFB] border border-[#E5E7EB] text-[#1A1D24] placeholder-[#9CA3AF] rounded-lg h-12 px-4 focus:border-[#2DD4BF] focus:ring-1 focus:ring-[#2DD4BF] outline-none transition-colors" />

                      </div>

                      {/* Company Field */}
                      <div>
                        <label htmlFor="company" className="block text-[#1A1D24] text-sm font-medium mb-2">
                          Azienda
                        </label>
                        <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        data-testid="contact-input-company"
                        placeholder="Nome della tua azienda"
                        className="w-full bg-[#F9FAFB] border border-[#E5E7EB] text-[#1A1D24] placeholder-[#9CA3AF] rounded-lg h-12 px-4 focus:border-[#2DD4BF] focus:ring-1 focus:ring-[#2DD4BF] outline-none transition-colors" />

                      </div>

                      {/* Message Field */}
                      <div>
                        <label htmlFor="message" className="block text-[#1A1D24] text-sm font-medium mb-2">
                          Messaggio *
                        </label>
                        <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        data-testid="contact-input-message"
                        placeholder="Raccontaci del tuo business e dei tuoi obiettivi..."
                        className="w-full bg-[#F9FAFB] border border-[#E5E7EB] text-[#1A1D24] placeholder-[#9CA3AF] rounded-lg p-4 focus:border-[#2DD4BF] focus:ring-1 focus:ring-[#2DD4BF] outline-none transition-colors resize-none" />

                      </div>

                      {/* Error Message */}
                      {error &&
                    <p className="text-red-500 text-sm">{error}</p>
                    }

                      {/* Submit Button */}
                      <button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="contact-submit-button"
                      className="w-full bg-[#2DD4BF] text-white px-8 py-4 font-semibold rounded-lg hover:bg-[#14B8A6] transition-all hover:scale-[1.02] shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2">

                        {isSubmitting ?
                      <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Invio in corso...
                          </> :

                      <>
                            Richiedi Consulenza
                            <ArrowUpRight size={20} />
                          </>
                      }
                      </button>
                    </div>
                  </form>
                }
              </div>
            </motion.div>

            {/* Trust Elements & Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-8">

              {/* Trust Elements */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-[#1A1D24]">
                  Perché Contattarci?
                </h3>
                {trustElements.map((item, index) =>
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-start gap-4 p-6 bg-white border border-[#E5E7EB] rounded-lg hover:shadow-md transition-shadow">

                    <div className="w-10 h-10 rounded-lg bg-[#2DD4BF]/10 flex items-center justify-center text-[#2DD4BF] flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="text-[#1A1D24] font-semibold mb-1">{item.title}</h4>
                      <p className="text-[#6B7280] text-sm">{item.description}</p>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Direct Contact */}
              <div className="p-8 bg-gradient-to-br from-[#2DD4BF]/10 to-[#2DD4BF]/5 border border-[#2DD4BF]/20 rounded-lg">
                <h3 className="text-xl font-semibold text-[#1A1D24] mb-4">
                  Preferisci l'Email?
                </h3>
                <p className="text-[#6B7280] mb-6">
                  Puoi anche raggiungerci direttamente a:
                </p>
                <a
                  href="mailto:contact@caroneconsulting.com"
                  data-testid="contact-email-link"
                  className="inline-flex items-center gap-3 text-[#2DD4BF] font-medium hover:gap-4 transition-all">
                  info@caroneconsulting.it
                  <Mail className="w-5 h-5" />

                </a>
              </div>

              {/* What to Expect */}
              <div className="p-8 bg-white border border-[#E5E7EB] rounded-lg">
                <h3 className="text-xl font-semibold text-[#1A1D24] mb-4">
                  Cosa Succede Dopo?
                </h3>
                <ol className="space-y-4">
                  {[
                  'Esaminiamo la tua richiesta entro 48 ore',
                  'Fissiamo una call conoscitiva secondo la tua disponibilità',
                  'Discutiamo i tuoi obiettivi e le sfide',
                  'Ricevi una proposta personalizzata'].
                  map((step, index) =>
                  <li key={step} className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#2DD4BF]/10 text-[#2DD4BF] text-sm font-semibold flex items-center justify-center flex-shrink-0">
                        {index + 1}
                      </span>
                      <span className="text-[#4B5563] text-sm">{step}</span>
                    </li>
                  )}
                </ol>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>);

};

export default ContactPage;